import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import MeService from '../services/core/MeService';
import TenantService from '../services/core/TenantService';
import UserPreferencesService from '../services/core/UserPreferencesService';
import { initializeTenantFromToken, clearTenantId, extractTenantIdFromToken, setTenantId } from '../services/tenantService';
import { clearDataAreaId } from '../services/dataAreaService';

const AuthContext = createContext(null);

// ============================================================================
// CONSTANTS
// ============================================================================

const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// ============================================================================
// STORAGE HELPERS - Support for "Remember Me" (localStorage vs sessionStorage)
// ============================================================================

/**
 * Obtener el storage correcto según el tipo de sesión
 * Si existe token en sessionStorage, usar sessionStorage
 * Si existe token en localStorage, usar localStorage
 * Por defecto usa localStorage para mantener compatibilidad
 */
const getStorage = () => {
  const tokenInLocal = localStorage.getItem(AUTH_TOKEN_KEY);
  const tokenInSession = sessionStorage.getItem(AUTH_TOKEN_KEY);
  
  if (tokenInSession) {
    return sessionStorage;
  } else if (tokenInLocal) {
    return localStorage;
  }
  // Default: usar localStorage
  return localStorage;
};

/**
 * Obtener token del storage correcto
 */
const getAuthToken = () => {
  const storage = getStorage();
  return storage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Obtener refresh token del storage correcto
 */
const getRefreshToken = () => {
  const storage = getStorage();
  return storage.getItem(REFRESH_TOKEN_KEY);
};

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(null);
  const [user, setUser] = useState(null);

  // Para Header.js (muestra rol)
  const roles = useMemo(() => {
    if (!user) return [];
    if (Array.isArray(user.roles)) return user.roles;
    if (typeof user.role === 'string' && user.role.trim()) return [`ROLE_${user.role.toUpperCase()}`];
    return [];
  }, [user]);

  const isSuperuser = useMemo(() => {
    return Boolean(user?.superuser ?? user?.isSuperuser);
  }, [user]);

  const permissions = useMemo(
    () => new Set(Array.isArray(user?.permissions) ? user.permissions : []),
    [user],
  );

  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    if (isSuperuser) return true;
    // The backend can disable legacy permissions for this deployment even when
    // the tenant owner has broad access to other modules. Follow /api/me here.
    if (permission.startsWith("legacy.") || permission.startsWith("sales.audit.")) {
      return permissions.has(permission);
    }
    const legacyRole = String(user?.role || "").toLowerCase();
    if (legacyRole === "owner" || legacyRole === "admin") return true;
    return permissions.has(permission);
  }, [isSuperuser, permissions, user]);

  const canManageTenant = useMemo(() => {
    return isSuperuser || Boolean(user?.tenantId);
  }, [isSuperuser, user]);

  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  const hydrateFromStorage = useCallback(() => {
    const token = getAuthToken();
    const refreshToken = getRefreshToken();
    const userData = localStorage.getItem('userData');

    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);

      // Store refresh token if present
      if (refreshToken) {
        const storage = getStorage();
        storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }

      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch {
          // ignore
        }
      }
    } else {
      setAuthToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // Cargar token/user del storage al iniciar
  useEffect(() => {
    hydrateFromStorage();
    
    // Initialize tenant from existing JWT token
    initializeTenantFromToken();
    
    // Initialize dataArea from localStorage (will be used by DataAreaSelector)
    const storedDataAreaId = localStorage.getItem('dataAreaId');
    if (process.env.NODE_ENV === 'development') {
      console.log('[AuthContext] Initialized dataAreaId from storage:', storedDataAreaId);
    }
    
    setIsLoading(false);

    // Si algún componente actualiza userData en localStorage (p.e. Login.js),
    // podemos rehidratar con un evento.
    const onUserDataChanged = () => hydrateFromStorage();
    window.addEventListener('userDataChanged', onUserDataChanged);

    return () => window.removeEventListener('userDataChanged', onUserDataChanged);
  }, [hydrateFromStorage]);


  /**
   * SINCRONIZACIÓN MULTI-TAB (storage event)
   *
   * Si otra pestaña cambia authToken o tenantId en localStorage
   * este listener sincroniza el estado React de esta pestaña.
   */
  useEffect(() => {

    const handleStorageChange = (event) => {

      if (event.key === AUTH_TOKEN_KEY || event.key === 'tenantId') {

        if (process.env.NODE_ENV === 'development') {
          console.log('[Auth Sync] storage change detected:', event.key);
        }

        hydrateFromStorage();
        initializeTenantFromToken();
      }

    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);

  }, [hydrateFromStorage]);


  // Sincronización con limpieza de sesión desde httpInterceptor (logout automático por 401/403)
  useEffect(() => {
    const handleSessionCleared = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AuthContext] Session cleared by httpInterceptor');
      }
      // Actualizar estado React para reflejar la limpieza de sesión
      setAuthToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setSelectedTenant(null);
      UserPreferencesService.clearCache();
    };

    window.addEventListener('sessionCleared', handleSessionCleared);

    return () => window.removeEventListener('sessionCleared', handleSessionCleared);
  }, []);

  // Login (si lo usas desde algún sitio)
  const login = useCallback((accessToken, refreshToken, userData, rememberMe = false) => {
    const storage = getStorage();
    
    storage.setItem(AUTH_TOKEN_KEY, accessToken);
    
    if (refreshToken) {
      storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }

    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('displayName', userData.name || userData.displayName);
      setUser(userData);
    }

    setAuthToken(accessToken);
    setIsAuthenticated(true);
  }, []);

  // Logout - Limpia ambos storages
  const logout = useCallback(() => {
    // Limpiar localStorage
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('userData');
    localStorage.removeItem('displayName');
    localStorage.removeItem('selectedTenant');
    localStorage.removeItem('selectedTenantId');
    localStorage.removeItem('selectedTenantCode');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('rememberMe');
    
    // Limpiar sessionStorage
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    
    // Clear tenantId from JWT
    clearTenantId();

    // Clear dataAreaId
    clearDataAreaId();
    UserPreferencesService.clearCache();

    // Dispatch event para que httpInterceptor y otros componentes actualicen su estado
    window.dispatchEvent(new Event('sessionCleared'));

    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);

    navigate('/');
  }, [navigate]);

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.warn('[Auth] No refresh token available');
      return false;
    }

    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        console.warn('[Auth] Refresh failed with status:', response.status);
        logout();
        return false;
      }

      const data = await response.json();
      const { accessToken, refreshToken: newRefreshToken } = data;

      const storage = getStorage();
      storage.setItem(AUTH_TOKEN_KEY, accessToken);
      if (newRefreshToken) {
        storage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      }
      setAuthToken(accessToken);

      if (process.env.NODE_ENV === 'development') {
        console.log('[Auth] Token refreshed successfully');
      }

      return true;
    } catch (error) {
      console.error('[Auth] Error refreshing token:', error);
      logout();
      return false;
    }
  }, [logout]);

  // Refrescar /api/me (útil tras switchTenant)
  const refreshMe = useCallback(async () => {
    const me = await MeService.me();
    localStorage.setItem('userData', JSON.stringify(me));
    if (me?.displayName) localStorage.setItem('displayName', me.displayName);
    setUser(me);
    window.dispatchEvent(new Event('userDataChanged'));
    return me;
  }, []);

  // Cargar tenants para selector
  const refreshTenants = useCallback(async () => {
    const me = await MeService.me();
    const userTenants = me?.tenants || [];
    
    if (isSuperuser) {
      try {
        const page = await TenantService.getAll(0, 200, { enabled: true });
        const list = page?.content || [];
        setTenants(list);
        
        const selectedTenantId = localStorage.getItem('selectedTenant');
        const current = selectedTenantId
          ? list.find((x) => String(x.id) === String(selectedTenantId))
          : list[0];
        
        if (current?.id) {
          localStorage.setItem('selectedTenant', String(current.id));
          setSelectedTenant(current);
        } else {
          setSelectedTenant(null);
        }
        
        return list;
      } catch (e) {
        setTenants(userTenants);
        if (userTenants.length > 0) {
          const currentTenant = userTenants[0];
          localStorage.setItem('selectedTenant', currentTenant.tenantId);
          setSelectedTenant(currentTenant);
        }
        return userTenants;
      }
    } else {
      setTenants(userTenants);
      
      const selectedTenantId = localStorage.getItem('selectedTenant');
      const current = selectedTenantId
        ? userTenants.find((x) => String(x.tenantId) === String(selectedTenantId))
        : userTenants[0];
      
      if (current?.tenantId) {
        localStorage.setItem('selectedTenant', current.tenantId);
        setSelectedTenant(current);
        setTenantId(current.tenantId);
      } else {
        setSelectedTenant(null);
      }
      
      return userTenants;
    }
  }, [isSuperuser]);

  // Cargar tenants al iniciar sesión
  useEffect(() => {
    refreshTenants().catch(() => { /* ignore */ });
  }, [refreshTenants]);

  const switchTenant = useCallback(async (tenant) => {
    const tenantId = tenant?.tenantId ?? tenant?.id ?? tenant;
    if (!tenantId) return;

    if (isSuperuser) {
      const resp = await MeService.switchTenant(tenantId);
      const newToken = resp?.accessToken;
      const newRefreshToken = resp?.refreshToken;
      if (newToken) {
        const storage = getStorage();
        storage.setItem(AUTH_TOKEN_KEY, newToken);
        if (newRefreshToken) {
          storage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        }
        setAuthToken(newToken);
        
        const newTenantId = extractTenantIdFromToken(newToken);
        if (newTenantId) {
          setTenantId(newTenantId);
        }
      }

      localStorage.setItem('selectedTenant', String(tenantId));
    } else {
      localStorage.setItem('selectedTenant', String(tenantId));
      setTenantId(tenantId);
    }

    await refreshMe();
    await refreshTenants();
  }, [isSuperuser, refreshMe, refreshTenants]);

  const value = {
    authToken,
    user,
    roles,
    permissions,
    hasPermission,
    isSuperuser,
    canManageTenant,

    tenants,
    selectedTenant,
    refreshTenants,
    switchTenant,

    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshMe,
    refreshAccessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
